import { createPortal } from 'react-dom';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ReactFormBuilder, ElementStore } from 'react-form-builder2';
import 'react-form-builder2/dist/app.css';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Bookmark as BookmarkIcon,
  Search as SearchIcon,
  SearchOff as SearchOffIcon,
} from '@mui/icons-material';
import { formApi, templateApi } from '../../services';
import { useNavigate } from 'react-router-dom';
import FormElementEdit from './FormElementEdit.tsx';
import { FormBuilderWrapper } from './FormBuilderWrapper';
import { FormBuilderProps, Form, FormTheme } from '../../types';
import { FormPreviewModal } from './FormPreviewModal';
import { ThemeEditor } from './ThemeEditor';
import { DEFAULT_THEME } from './defaultTheme';
import { AdvancedSettingsModal } from './AdvancedSettingsModal';
import { ProblemsDialog } from './ProblemsDialog';
import { collectFormProblems, customFieldProvides, inputFieldNames } from './formProblems';
import { useWorkflowTemplates } from './useWorkflowTemplates';
import { CustomField, WorkflowSettings } from '../../types';
import { generateThemeCSS, backgroundSx } from '../../utils/themeUtils';
import { formWidthSx } from '../../utils/formWidth';
import { items } from '../shared/formItems.ts';
import { ToolboxIconPortals } from '../shared/toolboxIcons.tsx';
import { ADMIN_COLORS, AdminThemeScope, FormSurfaceTheme } from '../../theme';
import { Toast } from '../shared/Toast.tsx';
import { BuilderHeader } from './BuilderHeader.tsx';
import CustomButton from '../CustomBlocks/Button';
import DatePicker from '../CustomBlocks/DatePicker.tsx';
import SlotBooking from '../CustomBlocks/SlotBooking';
import PaymentSection from '../CustomBlocks/PaymentSection';
import KycButton from '../CustomBlocks/KycButton';
import ESignButton from '../CustomBlocks/ESignButton';
import PhoneInput from '../CustomBlocks/PhoneInput.tsx';
import EmailInput from '../CustomBlocks/EmailInput.tsx';
import NameInput from '../CustomBlocks/NameInput.tsx';
import NumberInput from '../CustomBlocks/NumberInput.tsx';
import {
  SingleLineTextInput,
  MultiLineTextInput,
} from '../CustomBlocks/TextFieldInput';
import MultiSelectInput from '../CustomBlocks/MultiSelectInput';
import ChipSelectInput from '../CustomBlocks/ChipSelectInput';
import { RadioGroupInput } from '../CustomBlocks/ChoiceGroupInput';
import AiGenerator from '../CustomBlocks/AiGenerator';
import CrmAutocompleteInput from '../CustomBlocks/CrmAutocompleteInput';
import WorkflowPreviewBlock from '../CustomBlocks/WorkflowPreviewBlock';
import { QUIZ_BLOCK_COMPONENTS } from '../CustomBlocks/quiz';
import { SCALE_BLOCK_COMPONENTS } from '../CustomBlocks/scale';
import AddressInput, {
  DEFAULT_ADDRESS_ELEMENTS,
} from '../CustomBlocks/AddressInput.tsx';
import {
  DateInput,
  TimeInput,
  DateTimeLocalInput,
  MonthYearInput,
} from '../CustomBlocks/DateTimeInput.tsx';
import { registerComponent } from '../shared/utils.ts';
import { initializeAddedField } from './newFieldDefaults.ts';
import {
  validateFormFields,
  FormValidationIssue,
} from './validateForm.ts';
import { AppDialog } from '../shared/AppDialog.tsx';
import { DesktopIcon } from '../shared/Icons.tsx';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import StepBar from './StepBar.tsx';
import { ShareFormDialog } from '../form-list/ShareFormDialog.tsx';
import { publicFormUrl } from '../form-list/utils.ts';

// Width of the left property-editor rail. The idle-hint Box, the docked
// .edit-form (FormBuilderWrapper) and the canvas right-padding all key off this,
// so the option-list "Options / Value / Correct / +−" row has room to lay out.
const EDIT_RAIL_WIDTH = 440;

// Both side rails are fixed-width, so on narrower viewports they eat the canvas
// until the form preview is unusably clipped. Step them down instead of letting
// the middle column collapse. Widest tier first — first match wins.
// Header height. The fixed rails are offset by it, so it must come from one
// place — hardcoding it separately left the rails overlapping the header by 4px.
// (The step bar no longer contributes: it sits inside the canvas column, below
// the scroller, so it never offsets the rails.)
const HEADER_HEIGHT = 64;

const RAIL_TIERS = [
  { minWidth: 1400, edit: EDIT_RAIL_WIDTH, toolbox: 320 },
  { minWidth: 1100, edit: 360, toolbox: 290 },
  { minWidth: 0, edit: 310, toolbox: 260 },
];

const FormBuilderComponent: React.FC<FormBuilderProps> = ({
  tenantId,
  formId,
}) => {
  const navigate = useNavigate();

  const builderKey = formId || 'new';
  const isEditMode = formId && formId !== 'new';
  const [isMounted, setIsMounted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [formData, setFormData] = useState<any[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const isMultiStep = form?.formType === 'multistep';
  // Live count of fields on the builder canvas, read from the builder's own store
  // (subscription below). react-form-builder2 captures onPost ONCE, so relying on
  // formData alone can lag the Preview / Save-as-Template enable state; this store
  // count is the canvas source of truth and updates on every add/remove/reorder.
  const [builderFieldCount, setBuilderFieldCount] = useState(0);
  // Seeded rather than null: an unthemed form emits no theme stylesheet, and
  // those rules are what position a field's leading icon and reserve its
  // gutter — so a brand-new form rendered its icons on top of the placeholder
  // until the author opened the theme editor and applied something.
  const [formTheme, setFormTheme] = useState<FormTheme | null>(DEFAULT_THEME);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [shouldRenderBuilder, setShouldRenderBuilder] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [builderInstanceKey, setBuilderInstanceKey] = useState(0);
  const [themeEditorOpen, setThemeEditorOpen] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [workflowSettings, setWorkflowSettings] = useState<WorkflowSettings | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [shareForm, setShareForm] = useState<Form | null>(null);

  // Templates are needed to check a workflow's required fields, so they load as
  // soon as a form has workflow creation on (or Advanced settings is opened).
  const workflowTemplates = useWorkflowTemplates(
    tenantId,
    Boolean(workflowSettings?.enabled) || advancedSettingsOpen,
  );

  const formFieldNames = useMemo(() => new Set(inputFieldNames(formData)), [formData]);

  // AI Generator fields a workflow variable can be bound to, by field name.
  const aiFieldNames = useMemo(
    () =>
      (Array.isArray(formData) ? formData : [])
        .filter((field: any) => field?.key === 'AiGenerator')
        .map((field: any) => String(field?.field_name || field?.name || '').trim())
        .filter(Boolean),
    [formData],
  );

  // Names the submission can carry: the form's input fields plus custom fields
  // that supply a default or read the URL.
  const presentFieldNames = useMemo(
    () =>
      new Set([
        ...formFieldNames,
        ...customFields.filter(customFieldProvides).map((row) => row.key.trim()),
      ]),
    [formFieldNames, customFields],
  );

  const problems = useMemo(
    () =>
      collectFormProblems({
        fields: formData,
        workflowSettings,
        customFields,
        submitEveryStep: Boolean(form?.submitEveryStep),
        templates: workflowTemplates.templates,
        templatesFailed: workflowTemplates.error,
      }),
    [
      formData,
      workflowSettings,
      customFields,
      form?.submitEveryStep,
      workflowTemplates.templates,
      workflowTemplates.error,
    ],
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateTags, setTemplateTags] = useState<string[]>([]);
  const [templateScope, setTemplateScope] = useState<'private' | 'workspace' | 'global'>('private');
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [editingStepName, setEditingStepName] = useState('');
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
  const [stepToDelete, setStepToDelete] = useState<number | null>(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<
    FormValidationIssue[]
  >([]);
  const [toolboxQuery, setToolboxQuery] = useState('');
  const [toolboxNoResults, setToolboxNoResults] = useState(false);

  const currentStepRef = useRef(currentStep);
  const isMultiStepRef = useRef(isMultiStep);
  currentStepRef.current = currentStep;
  isMultiStepRef.current = isMultiStep;

  const hasFields =
    builderFieldCount > 0 || (Array.isArray(formData) && formData.length > 0);

  const isMobileViewport = useIsMobileViewport();
  const isWide = useMediaQuery(`(min-width:${RAIL_TIERS[0].minWidth}px)`);
  const isMedium = useMediaQuery(`(min-width:${RAIL_TIERS[1].minWidth}px)`);
  const railTier = isWide ? RAIL_TIERS[0] : isMedium ? RAIL_TIERS[1] : RAIL_TIERS[2];
  const editRailWidth = railTier.edit;
  const toolboxWidth = railTier.toolbox;
  // The step bar is docked at the BOTTOM now, so it no longer pushes the fixed
  // rails down — only the app header does.
  const railTop = HEADER_HEIGHT;

  // The property drawer is rendered by the library (.edit-form) and overlays
  // the canvas, so the canvas has to know when it is open to shift the form
  // paper out from under it. .edit-form is mounted/unmounted with the
  // selection, so the observer watches the preview CONTAINER (which is always
  // present) for the panel appearing and disappearing in its subtree.
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false);

  // The builder host is always mounted; the preview (and its .edit-form) is created
  // inside it later, so watch that one element rather than the whole page.
  const [builderHost, setBuilderHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!builderHost) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      const panel = builderHost.querySelector('.react-form-builder-preview .edit-form');
      setIsPropertyDrawerOpen(Boolean(panel && panel.childElementCount > 0));
    };
    sync();

    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(sync);
    });
    observer.observe(builderHost, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [builderHost]);

  const prevFieldCountRef = useRef<number | null>(null);


  const countScope = `${builderKey}:${dataLoaded ? 'ready' : 'loading'}:${isMultiStep ? currentStep : 'single'}`;
  const countScopeCurrentRef = useRef(countScope);
  countScopeCurrentRef.current = countScope;
  const countScopeRef = useRef<string | null>(null);


  // The last saved state, in two parts: the fields (saved by the Save button)
  // and the Advanced settings (also saved by that dialog's Apply button).
  const savedSignatureRef = useRef<{ fields: string; settings: string } | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const buildSignature = (
    fields: any[],
    theme: FormTheme | null,
    workflow: WorkflowSettings | null = null,
    custom: CustomField[] = [],
  ) => ({
    fields: JSON.stringify(
      (Array.isArray(fields) ? fields : []).map((f) => ({
        ...f,
        component: undefined,
      })),
    ),
    settings: JSON.stringify({ theme, workflow, custom }),
  });

  useEffect(() => {
    if (savedSignatureRef.current === null) return;
    const now = buildSignature(formData, formTheme, workflowSettings, customFields);
    setIsDirty(
      now.fields !== savedSignatureRef.current.fields ||
        now.settings !== savedSignatureRef.current.settings,
    );
  }, [formData, formTheme, workflowSettings, customFields]);

  useEffect(() => {
    if (!isDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  useEffect(() => {
    let active = true;
    const readCount = (state: any) => {
      if (!active) return;
      const data = state?.data;
      const nextCount = Array.isArray(data) ? data.length : 0;

      const scope = countScopeCurrentRef.current;
      if (countScopeRef.current !== scope) {
        countScopeRef.current = scope;
        prevFieldCountRef.current = nextCount;
        setBuilderFieldCount(nextCount);
        return;
      }

      if (
        prevFieldCountRef.current !== null &&
        nextCount > prevFieldCountRef.current
      ) {
        initializeAddedField(data as any[]);
      }

      prevFieldCountRef.current = nextCount;

      setBuilderFieldCount(nextCount);
    };
    try {
      const store = ElementStore as any;
      store.subscribe(readCount);
      readCount(store.state);
    } catch {
      // If the store shape changes across versions, fall back silently to formData.
    }
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Listener is on document (the .Sortable node is remounted by the
      // library), so scope it to the canvas explicitly.
      if (!target.closest('.react-form-builder-preview .Sortable')) return;

      if (target.closest('.toolbar-header-buttons')) return;
      if (target.closest('button, a, [contenteditable="true"]')) return;

      const item = target.closest<HTMLElement>('.rfb-item');
      const editBtn = item?.querySelector<HTMLElement>(
        '.toolbar-header-buttons .btn:has(.fa-edit)',
      );
      if (!editBtn) return;


      if (item?.classList.contains('is-editing')) return;
      editBtn.click();
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);


  useEffect(() => {
    const toolbar = document.querySelector('.react-form-builder-toolbar');
    if (!toolbar) return;

    const query = toolboxQuery.trim().toLowerCase();
    const sections = toolbar.querySelectorAll<HTMLElement>(':scope > ul > li');
    let totalVisible = 0;

    sections.forEach((section) => {
      const cards = section.querySelectorAll<HTMLElement>('.toolbar-group-item ul li');
      let visibleCount = 0;

      cards.forEach((card) => {
        const matches = !query || (card.textContent || '').toLowerCase().includes(query);
        if (matches) {
          card.style.removeProperty('display');
        } else {
          card.style.setProperty('display', 'none', 'important');
        }
        if (matches) visibleCount += 1;
      });

      if (visibleCount === 0) {
        section.style.setProperty('display', 'none', 'important');
      } else {
        section.style.removeProperty('display');
      }
      totalVisible += visibleCount;
    });

    setToolboxNoResults(!!query && totalVisible === 0);
  }, [toolboxQuery, builderInstanceKey, shouldRenderBuilder, currentStep]);

  registerComponent('CustomButton', CustomButton);
  registerComponent('DatePicker', DatePicker);
  registerComponent('SlotBooking', SlotBooking);
  registerComponent('PaymentSection', PaymentSection);
  registerComponent('KycButton', KycButton);
  registerComponent('ESignButton', ESignButton);
  registerComponent('PhoneInput', PhoneInput);
  registerComponent('EmailInput', EmailInput);
  registerComponent('NameInput', NameInput);
  registerComponent('NumberInput', NumberInput);
  registerComponent('AddressInput', AddressInput);
  registerComponent('DateInput', DateInput);
  registerComponent('TimeInput', TimeInput);
  registerComponent('DateTimeInput', DateTimeLocalInput);
  registerComponent('MonthYearInput', MonthYearInput);
  registerComponent('TextInput', SingleLineTextInput);
  registerComponent('TextArea', MultiLineTextInput);
  registerComponent('Tags', MultiSelectInput);
  registerComponent('ChipSelect', ChipSelectInput);
  registerComponent('RadioButtons', RadioGroupInput);
  registerComponent('AiGenerator', AiGenerator);
  registerComponent('CrmAutocomplete', CrmAutocompleteInput);
  registerComponent('WorkflowPreview', WorkflowPreviewBlock);
  Object.entries({ ...QUIZ_BLOCK_COMPONENTS, ...SCALE_BLOCK_COMPONENTS }).forEach(
    ([key, component]) => registerComponent(key, component),
  );

  useEffect(() => {
    setIsMounted(true);

    if (isEditMode) {
      setDataLoaded(false);
      loadForm();
    } else {
      setFormData([]);
      setForm(null);
      setDataLoaded(true);
      savedSignatureRef.current = buildSignature([], DEFAULT_THEME);
      setIsDirty(false);
    }

    return () => {
      setIsMounted(false);
      setDataLoaded(false);
    };
  }, [formId]);

  const loadForm = async () => {
    try {
      const loadedForm: Form = await formApi.getForm(
        tenantId,
        formId!,
      );
      setForm(loadedForm);

      if (
        loadedForm.formType === 'multistep' &&
        (!loadedForm.steps || loadedForm.steps.length === 0)
      ) {
        loadedForm.steps = ['Step 1', 'Step 2', 'Step 3'];
        setForm({ ...loadedForm, steps: loadedForm.steps });
      }

      let formDataArray: any[] = [];

      if (loadedForm && loadedForm.data) {
        if (Array.isArray(loadedForm.data)) {
          formDataArray = loadedForm.data;
        } else if (typeof loadedForm.data === 'object') {
          formDataArray = Object.keys(loadedForm.data)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((key) => loadedForm.data[key]);
        }

        // Self-heal `bare` on load: "description from another field" mode
        // (which set bare to skip the wrapper label) no longer exists, so a
        // field saved with bare=true would otherwise keep its label-less
        // rendering forever. Reassigned in place (not just passed to
        // setFormData) so the dirty-check signature below is built from this
        // same normalized array, rather than seeing bare as an unsaved change
        // on every load.
        formDataArray = formDataArray.map((field: any) =>
          field?.key === 'AiGenerator'
            ? { ...field, bare: false }
            : field,
        );
        setFormData(formDataArray);
      } else {
        setFormData([]);
      }

      if (loadedForm.theme) {
        setFormTheme(loadedForm.theme);
      }

      setWorkflowSettings(loadedForm.workflowSettings ?? null);
      setCustomFields(loadedForm.customFields ?? []);

      savedSignatureRef.current = buildSignature(
        formDataArray,
        loadedForm.theme || DEFAULT_THEME,
        loadedForm.workflowSettings ?? null,
        loadedForm.customFields ?? [],
      );
      setIsDirty(false);
    } catch (error) {
      setFormData([]);
      setForm(null);
    } finally {
      setDataLoaded(true);
    }
  };

  /**
   * Strip the live React component reference (it serializes to a useless `{}`)
   * and guarantee the address block's sub-field config is present. Both save
   * paths run fields through this so neither can persist a half-formed block.
   */
  const normalizeFieldForSave = (field: any) => {
    const { component, ...rest } = field || {};
    if (rest?.key !== 'AddressInput') return rest;
    return {
      ...rest,
      addressElements:
        rest.addressElements ??
        DEFAULT_ADDRESS_ELEMENTS.map((el) => ({ ...el })),
      showElementsLabel: rest.showElementsLabel ?? true,
    };
  };

  const handleSave = async () => {

    const issues = validateFormFields(formData);
    if (issues.length) {

      setValidationIssues(issues);
      return;
    }

    setSaving(true);
    try {
      const dataObject: Record<string, any> = {};
      formData.forEach((item, index) => {
        dataObject[index.toString()] = normalizeFieldForSave(item);
      });

      const updatePayload: any = {
        data: dataObject,
        status: 'draft',
      };

      if (isMultiStep && form?.steps) {
        updatePayload.steps = form.steps;
      }

      if (formTheme) {
        updatePayload.theme = formTheme;
      }

      // Workflow-on-submit and custom fields are edited in Advanced settings
      // and saved with the rest of the form.
      updatePayload.workflowSettings = workflowSettings;
      updatePayload.customFields = customFields;

      await formApi.updateForm(
        tenantId,
        formId!,
        updatePayload,
      );

      savedSignatureRef.current = buildSignature(
        formData,
        formTheme,
        workflowSettings,
        customFields,
      );
      setIsDirty(false);

      setSnackbarMessage('Form saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(
        error.isPaymentSync
          ? error.message
          : error.response?.data?.error || 'Error saving form',
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (name: string) => {
    try {
      await formApi.updateForm(tenantId, formId!, { name });
      setForm((prev) => (prev ? { ...prev, name } : prev));
      setSnackbarMessage('Form renamed');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error.message || 'Error renaming form');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw error;
    }
  };

  // Advanced settings edits apply to the builder live, so Cancel must undo them:
  // remember the settings as they were when the dialog opened.
  const advancedSnapshotRef = useRef<{
    theme: FormTheme | null;
    workflowSettings: WorkflowSettings | null;
    customFields: CustomField[];
  } | null>(null);

  const openAdvancedSettings = () => {
    advancedSnapshotRef.current = { theme: formTheme, workflowSettings, customFields };
    setAdvancedSettingsOpen(true);
  };

  const cancelAdvancedSettings = () => {
    const snapshot = advancedSnapshotRef.current;
    if (snapshot) {
      setFormTheme(snapshot.theme);
      setWorkflowSettings(snapshot.workflowSettings);
      setCustomFields(snapshot.customFields);
    }
    setAdvancedSettingsOpen(false);
  };

  // "Apply Settings" saves straight away; it does not wait for the Save button.
  // (A brand-new form has nothing to save to yet, so it just applies.)
  const applyAdvancedSettings = async (theme: FormTheme) => {
    setFormTheme(theme);
    if (isEditMode) {
      try {
        await formApi.updateForm(tenantId, formId!, {
          theme,
          workflowSettings,
          customFields,
        });
        // These settings are now saved; the fields may still have unsaved changes.
        if (savedSignatureRef.current) {
          savedSignatureRef.current = {
            ...savedSignatureRef.current,
            settings: buildSignature([], theme, workflowSettings, customFields).settings,
          };
        }
        setSnackbarMessage('Settings saved');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error: any) {
        setSnackbarMessage(
          (typeof error.response?.data === 'string' && error.response.data) ||
            error.message ||
            'Could not save settings',
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return; // keep the dialog open so nothing typed is lost
      }
    }
    setAdvancedSettingsOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };


  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      setSnackbarMessage('Please enter a template name');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setSaving(true);

      const dataObject: Record<string, any> = {};
      formData.forEach((field: any) => {
        if (field.id) {
          dataObject[field.id] = normalizeFieldForSave(field);
        }
      });

      await templateApi.createTemplate(tenantId, {
        name: templateName,
        description: templateDescription,
        tags: templateTags,
        formType: form?.formType || 'single',
        targetDevice: form?.targetDevice || 'desktop',
        configuration: dataObject,
        steps: form?.steps || [],
        theme: formTheme || form?.theme || null,
        visibility: (['global', 'workspace', 'private'].includes(templateScope) ? templateScope : 'private') as 'global' | 'workspace' | 'private',
      });

      setSnackbarMessage('Template saved successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setSaveTemplateOpen(false);
      setTemplateName('');
      setTemplateDescription('');
      setTemplateScope('private');
    } catch (error: any) {
      setSnackbarMessage(
        error.response?.data?.error || error.message || 'Error saving template'
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async (data: any) => {
    let extractedData: any[] = [];

    if (Array.isArray(data)) {
      extractedData = data;
    } else if (data && data.task_data) {
      if (Array.isArray(data.task_data)) {
        extractedData = data.task_data;
      } else if (typeof data.task_data === 'object') {
        extractedData = Object.values(data.task_data);
      }
    } else if (data && typeof data === 'object') {
      // defensive: if an object of fields is passed, convert to array
      extractedData = Object.values(data);
    }

    extractedData = extractedData.map((field: any) =>
      field?.key === 'AddressInput' && !field.addressElements
        ? {
            ...field,
            addressElements: DEFAULT_ADDRESS_ELEMENTS.map((el) => ({ ...el })),
            showElementsLabel: field.showElementsLabel ?? true,
          }
        : field,
    );

    const step = currentStepRef.current;

    if (isMultiStepRef.current) {
      const currentStepFields = Array.isArray(extractedData)
        ? extractedData.map(
            (field: any) => ({
              ...field,
              step,
            }),
          )
        : [];

      // Merge functionally so the first drop lands even if this closure's formData
      // is stale (onPost is captured once by the builder store).
      setFormData((prev) => {
        const fieldsFromOtherSteps = Array.isArray(prev)
          ? prev.filter(
              (field: any) =>
                field.step !== undefined && field.step !== step,
            )
          : [];
        return [...fieldsFromOtherSteps, ...currentStepFields];
      });
    } else {
      setFormData(Array.isArray(extractedData) ? extractedData : []);
    }
  };

  const getFieldsForCurrentStep = () => {
    if (!isMultiStep) {
      return Array.isArray(formData) ? formData : [];
    }
    const filtered = Array.isArray(formData)
      ? formData.filter(
          (field: any) =>
            field.step === undefined ||
            field.step === currentStep,
        )
      : [];
    return filtered;
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    if (newValue === currentStep) return;

    const prevData = formData;
    setShouldRenderBuilder(false);
    setFormData([]);

    setTimeout(() => {
      setCurrentStep(newValue);
      setTimeout(() => {
        setBuilderInstanceKey((k) => k + 1);
        setFormData(prevData);
        setShouldRenderBuilder(true);
      }, 150);
    }, 100);
  };

  const handleAddStep = () => {
    setForm((prev) => {
      const prevForm = prev || { name: 'Untitled Form', data: {}, steps: ['Step 1'] } as Form;
      const baseSteps = Array.isArray(prevForm.steps) && prevForm.steps.length > 0 ? prevForm.steps : ['Step 1'];
      const newSteps = [...baseSteps, `Step ${baseSteps.length + 1}`];

      setFormData((fd) => (Array.isArray(fd) ? fd : []));

      return { ...prevForm, steps: newSteps } as Form;
    });

    setShouldRenderBuilder(false);
    setTimeout(() => {
      setCurrentStep(() => {
        const last = (form?.steps?.length || 0) + 1;
        return Math.max(0, last - 1);
      });
      setShouldRenderBuilder(true);
    }, 120);
  };

  const handleRemoveStep = (stepIndex: number) => {
    setForm((prev) => {
      const prevForm = prev;
      if (!prevForm?.steps || prevForm.steps.length <= 1) return prevForm;

      const newSteps = prevForm.steps.filter((_, index) => index !== stepIndex);

      setFormData((fd) => {
        const arr = Array.isArray(fd) ? fd : [];
        const updated = arr
          .filter((field: any) => field.step !== stepIndex)
          .map((field: any) => {
            if (field.step !== undefined && field.step > stepIndex) {
              return { ...field, step: field.step - 1 };
            }
            return field;
          });
        return updated;
      });

      setCurrentStep((cs) => {
        if (stepIndex === cs) return Math.max(0, cs - 1);
        if (stepIndex < cs) return cs - 1;
        return cs;
      });

      return { ...prevForm, steps: newSteps } as Form;
    });
  };


  const handleStepNameChange = (
    index: number,
    newName: string,
  ) => {
    if (!form?.steps) return;
    const updatedSteps = [...form.steps];
    updatedSteps[index] =
      newName.trim() || `Step ${index + 1}`;
    setForm({ ...form, steps: updatedSteps });
    setEditingStepIndex(null);
    setEditingStepName('');
  };

  const handleStartEditStep = (index: number) => {
    setEditingStepIndex(index);
    setEditingStepName(
      form?.steps?.[index] || `Step ${index + 1}`,
    );
  };

  const handleCancelEditStep = () => {
    setEditingStepIndex(null);
    setEditingStepName('');
  };

  if (isMobileViewport) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: ADMIN_COLORS.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <AppDialog
          open
          onClose={() => navigate(`/tenant/${tenantId}/forms`)}
          title="Please switch to a larger screen"
          subtitle="The form editor needs more space than this screen offers. Please open this page on a desktop or tablet to create or edit forms."
          icon={<DesktopIcon />}
          actions={[
            {
              label: 'Back to forms',
              onClick: () => navigate(`/tenant/${tenantId}/forms`),
              variant: 'primary',
            },
          ]}
        />
      </Box>
    );
  }

  if (!dataLoaded && isEditMode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: ADMIN_COLORS.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={4} />
          <Typography
            variant="h6"
            sx={{
              mt: 3,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            Loading form builder...
          </Typography>
        </Box>
      </Box>
    );
  }


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: ADMIN_COLORS.canvas,
      }}
    >
      {/* ───────── STICKY HEADER ───────── */}
      <BuilderHeader
        height={HEADER_HEIGHT}
        formName={form?.name}
        isPublished={form?.status === 'published'}
        saving={saving}
        hasFields={hasFields}
        onBack={() => {
          if (isDirty) {
            setLeaveDialogOpen(true);
            return;
          }
          navigate(`/tenant/${tenantId}/forms`);
        }}
        onOpenTheme={() => setThemeEditorOpen(true)}
        onOpenAdvanced={openAdvancedSettings}
        problemCount={problems.length}
        hasProblemErrors={problems.some((p) => p.severity === 'error')}
        onOpenProblems={() => setProblemsOpen(true)}
        onPreview={() => setPreviewOpen(true)}
        onSaveAsTemplate={() => setSaveTemplateOpen(true)}
        onSave={handleSave}
        onRename={isEditMode ? handleRename : undefined}
        onShare={isEditMode ? () => setShareForm(form) : undefined}
      />

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: `${railTop}px`,
            width: toolboxWidth,
            zIndex: 1000,
            backgroundColor: ADMIN_COLORS.surface,
            borderRight: `1px solid ${ADMIN_COLORS.border}`,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search fields"
              value={toolboxQuery}
              onChange={(e) => setToolboxQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, height: 38, fontSize: 13, backgroundColor: ADMIN_COLORS.hover },
              }}
            />
          </Box>

          {toolboxNoResults && (
            <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
              <SearchOffIcon sx={{ fontSize: 32, color: ADMIN_COLORS.textFaint, mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                No fields found
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Try a different search term.
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ width: toolboxWidth, minWidth: toolboxWidth, flexShrink: 0 }} />


        <ToolboxIconPortals scanKey={currentStep} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 7,
            py: 4,
            backgroundColor: ADMIN_COLORS.canvas,
          }}
        >
          <FormBuilderWrapper
            ref={setBuilderHost}
            $railTop={railTop}
            $railWidth={editRailWidth}
            $drawerWidth={editRailWidth + 220}
            $toolboxWidth={toolboxWidth}
            $paperPaddingX={formTheme?.formPaddingX ?? 32}
          >
            <FormSurfaceTheme>
            <Paper
              sx={{
                ...formWidthSx(formTheme),
                py: `${formTheme?.formPaddingY ?? 32}px`,
                px: `${formTheme?.formPaddingX ?? 32}px`,
                borderRadius: 2.5,
                border: '1px solid #e6e9ed',
                boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
                minHeight: 'calc(100vh - 160px)',
                ...backgroundSx(formTheme),
              }}
            >
              <Box className="react-form-builder-preview" sx={{ width: '100%', position: 'relative' }}>
                <style>{generateThemeCSS(formTheme, '.react-form-builder-preview .Sortable')}</style>
                {!hasFields && shouldRenderBuilder && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 1,
                      pointerEvents: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      m: '24px',
                      px: 4,
                      py: 4,
                      border: '2px dashed #d5d5dd',
                      borderRadius: 3,
                      bgcolor: '#fafafc',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#eeeef2',
                        mb: 1.5,
                      }}
                    >
                      <Add sx={{ fontSize: 22, color: '#9c9ca8' }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#555', mb: 0.5 }}>
                      No fields added yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9c9ca8', maxWidth: 320 }}>
                      Drag a field from the toolbox on the left and drop it here to start building your form.
                    </Typography>
                  </Box>
                )}
                {isMounted && dataLoaded && shouldRenderBuilder && (
                  <ReactFormBuilder
                    key={`${builderKey}_${currentStep}_${builderInstanceKey}`}
                    editMode={true}
                    toolbarItems={items as any}
                    onLoad={async () => {
                      return new Promise<any>((resolve) => {
                        setTimeout(() => {
                          try {
                            const fieldsToLoad = isMultiStep ? getFieldsForCurrentStep() : formData;
                            const normalized = Array.isArray(fieldsToLoad)
                              ? fieldsToLoad
                              : fieldsToLoad
                              ? Object.values(fieldsToLoad)
                              : [];
                            if (typeof window !== 'undefined') {
                              (window as any).__formBuilderOnLoadData = normalized;
                            }
                            resolve(normalized);
                          } catch (e) {
                            if (typeof window !== 'undefined') {
                              (window as any).__formBuilderOnLoadError = e;
                            }
                            resolve([]);
                          }
                        }, 150);
                      });
                    }}
                    onPost={async (data: any) => {
                      try {
                        if (typeof window !== 'undefined') {
                          (window as any).__formBuilderOnPostData = data;
                        }
                        await handlePost(data);
                      } catch (e) {
                        if (typeof window !== 'undefined') {
                          (window as any).__formBuilderOnPostError = e;
                        }
                      }
                    }}
                    renderEditForm={(props: any) => (
                      <AdminThemeScope>
                        <FormElementEdit {...props} formFieldNames={Array.from(formFieldNames)} />
                      </AdminThemeScope>
                    )}
                  />
                )}
                {!shouldRenderBuilder && (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Loading step...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
            </FormSurfaceTheme>
          </FormBuilderWrapper>

        </Box>

        {isMultiStep && (
          <StepBar
            steps={form?.steps || ['Step 1']}
            currentStep={currentStep}
            editingStepIndex={editingStepIndex}
            editingStepName={editingStepName}
            deleteAnchorEl={deleteAnchorEl}
            stepToDelete={stepToDelete}
            onSelectStep={(index) => handleTabChange(null as any, index)}
            onAddStep={handleAddStep}
            onStartEditStep={handleStartEditStep}
            onCancelEditStep={handleCancelEditStep}
            onEditingStepNameChange={setEditingStepName}
            onCommitStepName={handleStepNameChange}
            onRequestDelete={(anchor, index) => {
              setDeleteAnchorEl(anchor);
              setStepToDelete(index);
            }}
            onCancelDelete={() => {
              setDeleteAnchorEl(null);
              setStepToDelete(null);
            }}
            onConfirmDelete={(index) => {
              handleRemoveStep(index);
              setDeleteAnchorEl(null);
              setStepToDelete(null);
            }}
          />
        )}
        </Box>

        {/* Dark backdrop behind the field properties drawer; clicking it closes the drawer. */}
        {isPropertyDrawerOpen && createPortal(
          <Box
            aria-hidden
            onClick={() => {
              (document.querySelector('.dismiss-edit') as HTMLElement | null)?.click();
            }}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 1250,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              animation: 'rfbBackdropIn 0.2s ease-out',
              '@keyframes rfbBackdropIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            }}
          />,
          document.body,
        )}
      </Box>

      <Toast
        open={snackbarOpen && validationIssues.length === 0}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      <Toast
        open={validationIssues.length > 0}
        severity="error"
        duration={6000}
        onClose={() => setValidationIssues([])}
        message={
          <Box>
            {validationIssues.map((issue, i) => (
              <Box
                key={`${issue.index}-${i}`}
                sx={{ display: 'flex', gap: 0.75, mt: i === 0 ? 0 : 0.25 }}
              >

                {issue.index >= 0 && (
                  <Box component="span" sx={{ color: '#8a8a96', flexShrink: 0 }}>
                    {issue.index + 1}.
                  </Box>
                )}
                <Box component="span">
                  <strong>{issue.blockName}</strong>
                  {issue.label && issue.label !== issue.blockName
                    ? ` (${issue.label})`
                    : ''}{' '}
                  {issue.message}
                </Box>
              </Box>
            ))}
          </Box>
        }
      />

      <AppDialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        title="Leave without saving?"
        subtitle="This form has changes that have not been saved. Leaving now discards them."
        actions={[
          {
            label: 'Keep editing',
            variant: 'secondary',
            onClick: () => setLeaveDialogOpen(false),
          },
          {
            label: 'Discard changes',
            onClick: () => {
              setLeaveDialogOpen(false);
              navigate(`/tenant/${tenantId}/forms`);
            },
          },
        ]}
      />

      <FormPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        formData={formData}
        formType={form?.formType}
        steps={form?.steps}
        theme={formTheme}
        onApplyTheme={(theme) => setFormTheme(theme)}
      />

      <ShareFormDialog
        open={Boolean(shareForm)}
        formName={shareForm?.name ?? ''}
        shareUrl={shareForm ? publicFormUrl(tenantId, shareForm.id) : ''}
        onClose={() => setShareForm(null)}
      />

      <ThemeEditor
        open={themeEditorOpen}
        onClose={() => setThemeEditorOpen(false)}
        theme={formTheme}
        isMultiStep={isMultiStep}
        onSave={(theme) => {
          setFormTheme(theme);
          setThemeEditorOpen(false);
        }}
        onChange={(theme) => {
          setFormTheme(theme);
        }}
      />

      <AdvancedSettingsModal
        open={advancedSettingsOpen}
        onClose={cancelAdvancedSettings}
        theme={formTheme}
        onSave={applyAdvancedSettings}
        onChange={(theme) => {
          setFormTheme(theme);
        }}
        workflowSettings={workflowSettings}
        onWorkflowSettingsChange={setWorkflowSettings}
        workflowTemplates={workflowTemplates}
        presentFieldNames={presentFieldNames}
        formFieldNames={formFieldNames}
        aiFieldNames={aiFieldNames}
        submitEveryStep={Boolean(form?.submitEveryStep)}
        customFields={customFields}
        onCustomFieldsChange={setCustomFields}
      />

      <ProblemsDialog
        open={problemsOpen}
        problems={problems}
        onClose={() => setProblemsOpen(false)}
      />

      <Dialog
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <BookmarkIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
              Save as Template
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Reuse this form's structure to start new forms faster
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ mb: 0.4, fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Template Name
            </Typography>
            <TextField
              autoFocus
              size="small"
              fullWidth
              variant="outlined"
              placeholder="e.g. Lead Capture Form"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
              InputProps={{ sx: { borderRadius: 1.5, height: 40, fontSize: 13 } }}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 0.4, fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Description
            </Typography>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              multiline
              minRows={2}
              placeholder="What is this template for?"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              InputProps={{ sx: { borderRadius: 1.5, fontSize: 13 } }}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 0.4, fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Tags
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={templateTags}
              onChange={(_, newValue) => setTemplateTags(newValue as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type and press Enter"
                  InputProps={{ ...params.InputProps, sx: { borderRadius: 1.5, fontSize: 13 } }}
                />
              )}
            />
          </Box>

          <Box>
            <Typography sx={{ mb: 0.4, fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
              Visibility
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={templateScope}
                onChange={(e) => {
                  const val = String(e.target.value);
                  const safe = val === 'global' || val === 'workspace' || val === 'private' ? val : 'private';
                  setTemplateScope(safe as 'private' | 'workspace' | 'global');
                }}
                MenuProps={{ PaperProps: { sx: { borderRadius: 1.5 } } }}
                sx={{
                  borderRadius: 1.5,
                  height: 40,
                  fontSize: 13,
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: ADMIN_COLORS.borderStrong },
                }}
              >
                <MenuItem value="private" sx={{ fontSize: 13 }}>
                  Private (Only visible to me)
                </MenuItem>
                <MenuItem value="workspace" sx={{ fontSize: 13 }}>
                  Workspace (Visible to my workspace)
                </MenuItem>
                <MenuItem value="global" sx={{ fontSize: 13 }}>
                  Global (Visible to everyone)
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setSaveTemplateOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAsTemplate}
            variant="contained"
            disableElevation
            disabled={!templateName.trim() || saving}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilderComponent;
